import React, { useState, useRef, memo, useEffect, useCallback } from 'react'
import { useRecurly } from '@recurly/react-recurly'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { colors } from '@pff-consumer/core-ui'
import { Button } from '@pff-consumer/tweed-ui/atoms/button/button'
import { FormField } from '@pff-consumer/tweed-ui/molecules/form-field/form-field.web'
import { Dropdown } from '@pff-consumer/tweed-ui/molecules/dropdown/dropdown.web'
import { subscribeFormSchema } from '@pff-consumer/utils/lib/accounts/subscribe-form-schema'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/browser-text'
import { yupResolver } from '@hookform/resolvers/yup'
import { Input } from '@pff-consumer/tweed-ui/atoms/input/input.web'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { renderDisclaimerText } from '@pff-consumer/utils/lib/accounts/subscribe/util-functions'
import {
  PaymentMethods,
  type SubscriptionInputFieldArgs,
  type SubscriptionFormInput,
  SubscriptionPlans,
} from '@pff-consumer/schema'
import { useStyles } from './credit-card-payment.styles'
import { CreditCardPaymentProps } from './credit-card-payment.props'
import { RecurlyCardElement } from './recurly-card-element/recurly-card-element.web'
import { countries } from '../../../shared/countries'

/**
 * This Credit Card form uses Recurly for the credit card inputs and native inputs for the rest.
 * The native inputs need some work to be more accessible and easier to use.
 *
 *
 * INPUTS:
 * They currently:
 *  - Can be tabbed through
 *  - Have a focused indicator (blue border)
 *  - To have an invalid indicator and description (recurly error has the details we could source)
 *
 * They still need:
 *  - Localization/i15n support. All of the address fields are simply text inputs at the moment, no localization for field names for state vs region
 *    or country selector, etc. Not sure if the gateway prefers "US", "USA", or "United States" as another example
 *
 * We should consider an address auto complete form approach
 *  - Google: https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete
 *  - Loqate: https://www.loqate.com/en-us/address-verification/address-capture/
 */

// These should probably come from Launch Darkly
const BUTTON_TEXT_ANNUAL_PLAN = 'Subscribe'
const BUTTON_TEXT_MONTHLY_PLAN = 'Subscribe'
const BUTTON_TEXT_LOADING = 'Processing Payment...'
const DEFAULT_COUNTRY_OPTION = { text: 'United States', value: 'US' }

export const CreditCardPayment: React.FC<CreditCardPaymentProps> = memo((props: CreditCardPaymentProps) => {
  const {
    isDisabled,
    isOnCompletePending,
    onComplete,
    onError,
    plan,
    price,
    onRecurlyTokenInit,
    isPaymentProcessing,
    isFormOpen,
  } = props
  const recurly = useRecurly()
  const formRef = useRef<HTMLFormElement>(null)
  const [isLocalPending, setIsLocalPending] = useState<boolean>(false)

  const isPending = isOnCompletePending || isLocalPending
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const tabIndex = isFormOpen ? 0 : -1
  const isAnnualPlan = plan === SubscriptionPlans.PREMIUM_ANNUAL
  const submitButtonText = isAnnualPlan ? BUTTON_TEXT_ANNUAL_PLAN : BUTTON_TEXT_MONTHLY_PLAN
  const [countryCode, setCountryCode] = useState<string>('US')
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, touchedFields },
  } = useForm<SubscriptionFormInput>({
    resolver: yupResolver(subscribeFormSchema),
    mode: 'onChange',
    shouldUseNativeValidation: true,
    defaultValues: {
      first_name: '',
      last_name: '',
      address1: '',
      city: '',
      state: '',
      country: DEFAULT_COUNTRY_OPTION.value,
      postal_code: '',
      cardDetails: {},
    },
  })

  const styles = useStyles({ isFormOpen, isPending })

  const processPayment: SubmitHandler<SubscriptionFormInput> = useCallback(() => {
    onRecurlyTokenInit(PaymentMethods.CREDIT_CARD)

    recurly.token(formRef.current!, (error, token: { id: string }) => {
      if (error) {
        onError(error)
      } else if (token) {
        onComplete(token.id, 'credit_card')
      }
    })

    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: submitButtonText,
      ctaLocation: 'Single Page Checkout',
    })
  }, [])

  const handleFormSubmit = useCallback(() => {
    handleSubmit(processPayment)()
  }, [handleSubmit, processPayment])

  useEffect(() => {
    if (isPending) {
      setIsLocalPending(false)
    }
  }, [isPending])

  const country = countries.find(({ countryShortCode }) => countryShortCode === countryCode)
  const regions = country ? country.regions : []

  const renderInputField = (args: SubscriptionInputFieldArgs) => {
    const { name, label, disabled = false, type = 'text' } = args
    const isError = !!errors[name]?.message

    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, onBlur } }) => (
          <FormField errorMessage={errors[name]?.message}>
            <Input
              dataRecurly={name}
              id={label}
              type={type}
              placeholder={label}
              value={value as string}
              onChange={onChange}
              disabled={disabled}
              isError={isError}
              onBlur={onBlur}
            />
          </FormField>
        )}
      />
    )
  }

  const handleCountryChange = (value: string, onChange: (...event: any[]) => void) => {
    onChange(value)
    setCountryCode(value)
  }

  return (
    <>
      <form
        ref={formRef}
        className={styles.container}
        autoComplete='on'
      >
        <Text variant={TextDescriptionVariant.REGULAR_PRIMARY_REGULAR}>
          Card Details<span className={styles.mandatoryIndicator}>*</span>
        </Text>
        <div>
          <div className={styles.fieldsContainer}>
            {renderInputField({ name: 'first_name', label: 'First Name' })}
            {renderInputField({ name: 'last_name', label: 'Last Name' })}
          </div>

          <Controller
            control={control}
            name='cardDetails'
            render={({ field: { onChange, onBlur } }) => (
              <FormField errorMessage={touchedFields.cardDetails || isSubmitted ? errors.cardDetails?.message : ''}>
                <RecurlyCardElement
                  tabIndex={`${tabIndex}`}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              </FormField>
            )}
          />

          {renderInputField({ name: 'address1', label: 'Address' })}
          {renderInputField({ name: 'city', label: 'City' })}

          <div className={styles.fieldsContainer}>
            <Controller
              control={control}
              name='state'
              render={({ field: { onChange } }) => (
                <FormField errorMessage={errors.state?.message}>
                  <Dropdown
                    id='state'
                    dataRecurly='state'
                    placeholder='State'
                    options={regions.map(({ shortCode, name }) => ({ text: name, value: shortCode! }))}
                    onSelectOption={(value) => onChange(value.value)}
                    isError={!!errors.state?.message}
                    containerStyles={styles.dropdowns}
                  />
                </FormField>
              )}
            />
            <Controller
              control={control}
              name='country'
              render={({ field: { onChange } }) => (
                <FormField errorMessage={errors.country?.message}>
                  <Dropdown
                    id='country'
                    dataRecurly='country'
                    defaultOption={DEFAULT_COUNTRY_OPTION}
                    options={countries.map(({ countryShortCode, countryName }) => ({
                      text: countryName,
                      value: countryShortCode,
                    }))}
                    onSelectOption={(value) => handleCountryChange(value.value, onChange)}
                    isError={!!errors.country?.message}
                    containerStyles={styles.dropdowns}
                  />
                </FormField>
              )}
            />
          </div>
          {renderInputField({ name: 'postal_code', label: 'Zip Code' })}
        </div>
        <div className={styles.button}>
          <Button
            size='lg'
            accessibilityLabel='Subscribe Button'
            onPress={() => handleFormSubmit()}
            isLoading={isPending && isPaymentProcessing}
            disabled={isDisabled || isPending}
            loadingText={BUTTON_TEXT_LOADING}
            title={submitButtonText}
            data-analytics-id='credit-card-form-submit'
          />
        </div>
      </form>

      {isFormOpen && (
        <div className={styles.disclaimer}>
          <Text
            variant={TextDescriptionVariant.XSMALL_PRIMARY_REGULAR}
            colorOverride={colors.subscriptionDescriptionText}
          >
            {renderDisclaimerText(price, submitButtonText, isAnnualPlan)}
          </Text>
        </div>
      )}
    </>
  )
})
